"use client"

import clsx from "clsx"
import { ReactNode, useEffect } from "react"
import { npPagePxClassName } from "src/components/np-design/components/constants"
import { useInViewEffectOncePerPage } from "src/hooks/useInViewEffectOncePerPage"
import { useIsClient } from "src/hooks/useIsClient"
import { MissingConsentNotice } from "src/integrations/lib/MissingConsentNotice"
import { trackViewEmbed } from "src/tracking/events"
import { useCcm19EmbeddingAccepted } from "../ccm19/useConsented"
import { integrationConsentId } from "./config"
import { setIntegrationNeeded } from "./needed"
import { IntegrationId } from "./types"

export type IntegrationEmbedPreviewProps = {
  id: string | undefined
  showFallback?: boolean
}

export type IntegrationEmbedProps = IntegrationEmbedPreviewProps & {
  integrationId: IntegrationId
  children: ReactNode
}

export const IntegrationEmbed = ({ integrationId, id: embedId, showFallback, children }: IntegrationEmbedProps) => {
  const isClient = useIsClient()
  const consentId = integrationConsentId[integrationId]
  const consented = useCcm19EmbeddingAccepted(consentId)

  useEffect(() => {
    setIntegrationNeeded(integrationId, true)
  }, [integrationId])

  const effectId = `${integrationId} | ${embedId}`

  const ref = useInViewEffectOncePerPage({
    id: effectId,
    effect: () => {
      if (embedId) {
        trackViewEmbed({ integrationId, embedId })
      }
    },
  })

  if (!isClient) {
    return null
  }

  if (!consented && showFallback) {
    return (
      <div className="rounded-3xl border">
        <MissingConsentNotice consentId={consentId} />
      </div>
    )
  }

  if (!consented) {
    return null
  }

  return (
    <div ref={ref} className={clsx(npPagePxClassName, "size-full")}>
      {children}
    </div>
  )
}
